/* eslint-disable react-refresh/only-export-components -- cell renderers module: exports the `renderCell` helper alongside internal cell components; not a fast-refresh component module. */
import { formatDistanceToNow } from "date-fns";
import { Check, Copy, ExternalLink, FileText, Minus, X } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { type TColumnDef, type TColumnMeta } from "../types";
import { getColumnValue } from "../utils";

function asString(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  if (typeof value === "symbol") return value.toString();
  return JSON.stringify(value) ?? "";
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) return Number(value);
  return null;
}

function toDate(value: unknown): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
}

const EMPTY = <span className="text-muted-foreground">-</span>;

function TextCell({ value }: { value: unknown }) {
  const text = asString(value);
  if (!text) return EMPTY;
  return <span className="text-foreground">{text}</span>;
}

function BadgeCell({ value, meta }: { value: unknown; meta?: TColumnMeta }) {
  const text = asString(value);
  if (!text) return EMPTY;
  const variant = meta?.badgeVariant?.(value) ?? "secondary";
  return <Badge variant={variant}>{text}</Badge>;
}

/** Status: colored dot + label; picks tone from common status keywords. */
function StatusCell({ value }: { value: unknown }) {
  const text = asString(value);
  if (!text) return EMPTY;
  const lower = text.toLowerCase();
  const tone =
    /(active|success|completed|approved|done|healthy|paid|online)/.test(lower)
      ? "bg-green-500"
      : /(pending|processing|in_progress|warning|queued)/.test(lower)
        ? "bg-amber-500"
        : /(failed|error|inactive|blocked|rejected|banned|offline|expired)/.test(lower)
          ? "bg-red-500"
          : "bg-muted-foreground";
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={cn("h-2 w-2 rounded-full", tone)}
        aria-hidden
      />
      <span className="capitalize text-foreground">{text.replace(/_/g, " ")}</span>
    </span>
  );
}

type TUserValue = {
  name?: string;
  full_name?: string;
  email?: string;
  avatar?: string;
  avatar_url?: string;
};

function UserCell({ value }: { value: unknown }) {
  const user = (value ?? {}) as TUserValue;
  const name = user.name ?? user.full_name ?? "";
  const email = user.email ?? "";
  if (!name && !email) return EMPTY;
  return (
    <div className="flex items-center gap-2">
      <Avatar
        size="sm"
        name={name || email}
        src={user.avatar ?? user.avatar_url}
        alt={name || email}
      />
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-foreground">{name || email}</div>
        {name && email && <div className="truncate text-xs text-muted-foreground">{email}</div>}
      </div>
    </div>
  );
}

function EmailCell({ value }: { value: unknown }) {
  const email = asString(value);
  if (!email) return EMPTY;
  return (
    <a
      href={`mailto:${email}`}
      className="text-primary hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {email}
    </a>
  );
}

type TCompanyValue = { name?: string; logo?: string; logo_url?: string };

function CompanyCell({ value }: { value: unknown }) {
  if (typeof value === "string") return <TextCell value={value} />;
  const company = (value ?? {}) as TCompanyValue;
  if (!company.name) return EMPTY;
  return (
    <div className="flex items-center gap-2">
      <Avatar
        size="sm"
        name={company.name}
        src={company.logo ?? company.logo_url}
        alt={company.name}
      />
      <span className="truncate text-sm font-medium text-foreground">{company.name}</span>
    </div>
  );
}

function BooleanCell({ value }: { value: unknown }) {
  if (value == null) return <Minus className="h-4 w-4 text-muted-foreground" />;
  return value ? (
    <Check
      className="h-4 w-4 text-green-600"
      aria-label="Yes"
    />
  ) : (
    <X
      className="h-4 w-4 text-muted-foreground"
      aria-label="No"
    />
  );
}

function DateCell({ value }: { value: unknown }) {
  const date = toDate(value);
  if (!date) return EMPTY;
  return (
    <span className="text-sm text-muted-foreground">
      {date.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
    </span>
  );
}

function RelativeTimeCell({ value }: { value: unknown }) {
  const date = toDate(value);
  if (!date) return EMPTY;
  return (
    <span
      className="text-sm text-muted-foreground"
      title={date.toLocaleString()}
    >
      {formatDistanceToNow(date, { addSuffix: true })}
    </span>
  );
}

function CurrencyCell({ value, meta }: { value: unknown; meta?: TColumnMeta }) {
  const num = asNumber(value);
  if (num == null) return EMPTY;
  const formatted = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: meta?.currency ?? "USD",
    minimumFractionDigits: meta?.fractionDigits ?? 2,
    maximumFractionDigits: meta?.fractionDigits ?? 2,
  }).format(num);
  return <span className="tabular-nums text-foreground">{formatted}</span>;
}

function PercentageCell({ value, meta }: { value: unknown; meta?: TColumnMeta }) {
  const num = asNumber(value);
  if (num == null) return EMPTY;
  const digits = meta?.fractionDigits ?? 1;
  return <span className="tabular-nums text-foreground">{num.toFixed(digits)}%</span>;
}

function TagsCell({ value }: { value: unknown }) {
  const tags = Array.isArray(value) ? value.map(asString).filter(Boolean) : [];
  if (tags.length === 0) return EMPTY;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag) => (
        <Badge
          key={tag}
          variant="outline"
        >
          {tag}
        </Badge>
      ))}
    </div>
  );
}

function LinkCell({ value }: { value: unknown }) {
  const href = asString(value);
  if (!href) return EMPTY;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="max-w-[16rem] truncate">{href}</span>
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  );
}

function IconCell({ value }: { value: unknown }) {
  if (value == null) return EMPTY;
  // If a node was provided via accessor, render it directly.
  return <span className="inline-flex items-center">{value as ReactNode}</span>;
}

type TFileValue = { name?: string; filename?: string; url?: string };

function FileCell({ value }: { value: unknown }) {
  const file = typeof value === "string" ? ({ name: value } as TFileValue) : ((value ?? {}) as TFileValue);
  const name = file.name ?? file.filename ?? "";
  if (!name) return EMPTY;
  const content = (
    <span className="inline-flex items-center gap-1.5 text-foreground">
      <FileText className="h-4 w-4 text-muted-foreground" />
      <span className="max-w-[16rem] truncate">{name}</span>
    </span>
  );
  if (file.url) {
    return (
      <a
        href={file.url}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </a>
    );
  }
  return content;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label="Copy to clipboard"
      className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function UuidCell({ value }: { value: unknown }) {
  const uuid = asString(value);
  if (!uuid) return EMPTY;
  const short = uuid.length > 12 ? `${uuid.slice(0, 8)}…` : uuid;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="font-mono text-xs text-muted-foreground"
        title={uuid}
      >
        {short}
      </span>
      <CopyButton text={uuid} />
    </span>
  );
}

function JsonCell({ value }: { value: unknown }) {
  const [open, setOpen] = useState(false);
  if (value == null) return EMPTY;
  const json = JSON.stringify(value, null, 2);
  return (
    <div className="text-xs">
      <button
        type="button"
        className="text-primary hover:underline"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {open ? "Hide" : "View"} JSON
      </button>
      {open && (
        <pre className="mt-1 max-h-48 max-w-md overflow-auto rounded bg-muted p-2 font-mono text-muted-foreground">
          {json}
        </pre>
      )}
    </div>
  );
}

function ProgressCell({ value }: { value: unknown }) {
  const num = asNumber(value);
  if (num == null) return EMPTY;
  const pct = Math.max(0, Math.min(100, num));
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tabular-nums text-xs text-muted-foreground">{pct.toFixed(0)}%</span>
    </div>
  );
}

function ScoreCell({ value }: { value: unknown }) {
  const num = asNumber(value);
  if (num == null) return EMPTY;
  const tone =
    num >= 75 ? "text-green-600" : num >= 50 ? "text-amber-600" : "text-red-600";
  return <span className={cn("font-semibold tabular-nums", tone)}>{num}</span>;
}

/**
 * Renders a cell for the given column + row: custom `cell` wins, then the
 * built-in `type` renderer, then plain text of the resolved value.
 */
export function renderCell<TRow>(column: TColumnDef<TRow>, row: TRow): ReactNode {
  if (column.cell) return column.cell(row);
  const value = getColumnValue(column, row);
  switch (column.type) {
    case "badge":
      return (
        <BadgeCell
          value={value}
          meta={column.meta}
        />
      );
    case "status":
      return <StatusCell value={value} />;
    case "user":
      return <UserCell value={value} />;
    case "email":
      return <EmailCell value={value} />;
    case "company":
      return <CompanyCell value={value} />;
    case "boolean":
      return <BooleanCell value={value} />;
    case "date":
      return <DateCell value={value} />;
    case "relativeTime":
      return <RelativeTimeCell value={value} />;
    case "currency":
      return (
        <CurrencyCell
          value={value}
          meta={column.meta}
        />
      );
    case "percentage":
      return (
        <PercentageCell
          value={value}
          meta={column.meta}
        />
      );
    case "tags":
      return <TagsCell value={value} />;
    case "link":
      return <LinkCell value={value} />;
    case "icon":
      return <IconCell value={value} />;
    case "file":
      return <FileCell value={value} />;
    case "uuid":
      return <UuidCell value={value} />;
    case "json":
      return <JsonCell value={value} />;
    case "progress":
      return <ProgressCell value={value} />;
    case "score":
      return <ScoreCell value={value} />;
    case "text":
    default:
      return <TextCell value={value} />;
  }
}
